/**
 * Maps the backend's flat validation errors ({"dbServer.sharedServerId": [...],
 * "non_field_errors": [...]}) back onto form fields and onto the steps of the
 * standard New Deployment wizard:
 *   3 Infrastructure & Settings: customer, environment, region, CI/CD, model, servers, network, tags
 *   4 Application (ERP):         app settings
 *   5 Review & Deploy:            everything without a field of its own
 * Revision 4: network.vnetCidr / network.subnetCidr (e.g. an overlap) and
 * network.existingNetworkId land on the Network section's own inputs.
 * A dedicated DB's role-tagged `disks` list has no form field of its own: dbServer.disks.<n>.sizeGb|type
 * land on the data / log / TempDB row that disk came from (the n-th disk of the flattened list, found
 * from the form's storage), and a list-level dbServer.disks message ("Add at least one data disk", more
 * than 16) or a role error lands on the storage panel (dbServer.storage.total). dbServer.sqlLogin,
 * sqlAuthentication, storageWorkload and engineVersion keep their own controls.
 */
import type { DbStorageFormValue } from '../../types/workwise'
import { DB_STORAGE_PANEL_PATH, dbDiskRoleAt } from './dbStorage'

export const INFRA_STEP = 3
export const APPLICATION_STEP = 4
export const REVIEW_STEP = 5

/**
 * The step Next goes to. With the Application (ERP) step skipped (infrastructure only) step 3 goes straight to
 * Review & Deploy; every other move is the usual one step on.
 */
export function stepAfter(step: number, skipApplication: boolean): number {
  return skipApplication && step === INFRA_STEP ? REVIEW_STEP : step + 1
}

/** The step Back goes to: from Review it skips the skipped Application (ERP) step; otherwise one step back. */
export function stepBefore(step: number, skipApplication: boolean): number {
  return skipApplication && step === REVIEW_STEP ? INFRA_STEP : Math.max(1, step - 1)
}

export interface MappedFieldError {
  /** react-hook-form path, e.g. "appServer.disks.0.sizeGb". */
  path: string
  message: string
  step: number
}

/** What the mapping needs from the form: the DB storage rows, to place a flattened disk's error on its row. */
export interface ServerErrorContext {
  dbStorage?: DbStorageFormValue
}

export interface MappedServerErrors {
  fields: MappedFieldError[]
  /** Errors with no field of their own (non_field_errors, correlationId, ...). */
  general: string[]
  /** The earliest step that has any field error, or null. */
  firstStep: number | null
}

const STEP_BY_ROOT: Record<string, number> = {
  customer: INFRA_STEP,
  environment: INFRA_STEP,
  region: INFRA_STEP,
  cicd: INFRA_STEP,
  cloudProvider: INFRA_STEP,
  deploymentModel: INFRA_STEP,
  appServer: INFRA_STEP,
  dbServer: INFRA_STEP,
  gatewayServer: INFRA_STEP,
  network: INFRA_STEP,
  tags: INFRA_STEP,
  app: APPLICATION_STEP,
}

/**
 * Payload paths with no input of their own in the form: the DB and gateway
 * modes are both set by the one Deployment Model radio (revision 3), so a
 * mixed-mode 400 at gatewayServer.mode lands there.
 */
const PATH_ALIASES: Record<string, string> = {
  'dbServer.mode': 'deploymentModel',
  'gatewayServer.mode': 'deploymentModel',
}

const DB_DISK_PATH = /^dbServer\.disks(?:\.(\d+)(?:\.(\w+))?)?$/

/** A dedicated DB disk path (flattened list) -> the control of its storage row; null for any other path. */
function dbDiskControlPath(path: string, context: ServerErrorContext): string | null {
  const match = DB_DISK_PATH.exec(path)
  if (!match) return null
  const [, index, key] = match
  if (index === undefined || (key !== 'sizeGb' && key !== 'type')) return DB_STORAGE_PANEL_PATH
  const role = context.dbStorage ? dbDiskRoleAt(context.dbStorage, Number(index)) : null
  return role ? `dbServer.storage.${role}.${key}` : DB_STORAGE_PANEL_PATH
}

/**
 * "appServer.disks[0].sizeGb" -> "appServer.disks.0.sizeGb"; mode paths -> "deploymentModel"; a dedicated
 * DB's disk paths -> its storage row (see ServerErrorContext).
 */
export function normalizeErrorPath(path: string, context: ServerErrorContext = {}): string {
  const normalized = path.replace(/\[(\d+)\]/g, '.$1').replace(/^\.+|\.+$/g, '')
  return PATH_ALIASES[normalized] ?? dbDiskControlPath(normalized, context) ?? normalized
}

/** The wizard step that owns a (normalised) path; Review for unknown ones. */
export function stepForPath(path: string): number {
  const root = normalizeErrorPath(path).split('.')[0]
  return STEP_BY_ROOT[root] ?? REVIEW_STEP
}

export function mapServerErrors(
  errors: Record<string, string[]>,
  general: string[] = [],
  context: ServerErrorContext = {},
): MappedServerErrors {
  const fields: MappedFieldError[] = []
  const generalOut = [...general]
  for (const [rawPath, messages] of Object.entries(errors)) {
    const path = normalizeErrorPath(rawPath, context)
    const root = path.split('.')[0]
    if (!(root in STEP_BY_ROOT)) {
      // correlationId, estimatedMonthlyCost, non_field_errors, ...: no input to attach to.
      generalOut.push(...messages)
      continue
    }
    for (const message of messages) fields.push({ path, message, step: stepForPath(path) })
  }
  const steps = fields.map((f) => f.step)
  return { fields, general: generalOut, firstStep: steps.length ? Math.min(...steps) : null }
}
